import { AttachmentFileIcon } from "@/components";
import { EditorInput, MenuBar, useInputEditor } from "@/components/RichText";
import { useCheckFileDrop } from "@/hooks/useCheckFileDrop";
import { SocketManager, StorageService } from "@/services";
import useChatStore from "@/store/chatStore";
import { ESendMessageType } from "@/types";
import {
  checkImageFromMimeType,
  checkVideoFromMimeType,
  cn,
  toastError,
} from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoMdSend } from "react-icons/io";
import { MdClose, MdErrorOutline, MdFileUpload } from "react-icons/md";
import { useFileUpload, MAX_IMAGE_SIZE } from "../../hooks/useFileUpload";
import { ChatInputSchema, ChatInputType } from "../../schemas/ChatInputSchema";

interface Props {
  roomId: number;
}

const MAX_FILES = 12;

const ChatInput: React.FC<Props> = ({ roomId }) => {
  const isConnectionReady = useChatStore((state) => state.isConnectionReady);
  const { editor, setEditorContent } = useInputEditor();

  // State to show upload file status
  // 1 means uploading, 0 | undefined means not uploading, -1 means upload failed
  const [uploadStatuses, setUploadStatuses] = useState<number[]>([]);

  const [isSending, setIsSending] = useState(false);

  // State to show failed upload files
  const [failedUploads, setFailedUploads] = useState<{ file: File }[]>([]);

  // form control chat input
  const { control, handleSubmit, formState, reset } = useForm({
    defaultValues: {
      message: "",
      files: [],
    },
    resolver: zodResolver(ChatInputSchema),
  });

  // handle file array in form
  const { fields, remove, append } = useFieldArray({ control, name: "files" });

  // control file upload
  const { getInputProps, getRootProps, open } = useFileUpload({
    handleFileAccepted: (acceptedFiles) => {
      // update files to form field
      // max number of files is 10
      if (fields.length > 10) return;
      append(acceptedFiles.map((file) => ({ file })));
    },
    enable: !isSending,
  });

  // Reset form when roomId change
  useEffect(() => {
    reset();
    setEditorContent("");
  }, [roomId]);

  // Upload files and get public urls
  const publicFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return [];
    // set loading
    setUploadStatuses(files.map(() => 1));

    const result = await StorageService.uploadFiles(files);

    // update upload statuses
    setUploadStatuses(result.map((url) => (url ? 0 : -1)));

    // set failed uploads
    const failedFiles = files
      ?.filter((_, index) => result[index] === false)
      .map((file) => ({ file }));
    setFailedUploads(failedFiles || []);

    return result.filter((url): url is string => url !== false);
  }, []);

  // handle send message button click
  const handleSendMessage = (data: ChatInputType) => {
    if (!isConnectionReady) return;

    // set loading button send
    setIsSending(true);

    // Validate number of files
    if (data.files && data.files.length > MAX_FILES) {
      toastError(`ファイルは${MAX_FILES}つまでアップロードできます`);
      setIsSending(false);
      return;
    }

    // upload all files to s3
    const files = data.files?.map((fileItem) => fileItem.file) || [];
    publicFiles(files)
      .then((successedUploadFiles) => {
        // result is array of public url

        const socketManager = SocketManager.getInstance();

        if (data.message)
          socketManager.emitMessage({
            roomId: roomId.toString(),
            message: data.message,
            type: ESendMessageType.TEXT,
          });

        if (successedUploadFiles.length) {
          // handle emit socket send_files event
          socketManager.emitMessage({
            roomId: roomId.toString(),
            files: successedUploadFiles,
            type: ESendMessageType.FILES,
          });
        }

        // reset form
        reset();
        setUploadStatuses([]);
        setEditorContent("");

        // remove loading button
        setTimeout(() => {
          setIsSending(false);
        }, 100);
      })
      .catch((error) => {
        toastError(error.message || "ファイルのアップロードに失敗しました");
        // remove loading button
        setIsSending(false);
        setUploadStatuses([]);
      });

    // send message via socket connection with data
  };

  // remove file selected
  const handleRemoveFile = (index: number) => {
    remove(index);
  };

  // Check file is dragging over to show absolute dropzone
  const { isDragOver } = useCheckFileDrop();

  // handle click icon upload to open file dialog
  const handleUploadClick = () => {
    open(); // Mở file dialog thủ công
  };

  // Press Ctrl + Enter to send message
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      handleSubmit(handleSendMessage)(); // Gọi submit function
    }
  };

  // Paste screenshot / image from clipboard
  const handleOnPaste = (pastedFiles: File[]) => {
    if (isSending) return;

    if (fields.length + pastedFiles.length > MAX_FILES) {
      toastError(`ファイルは${MAX_FILES}つまでアップロードできます`);
      return;
    }

    const validPastedFiles = pastedFiles.filter((file) => {
      if (file.size > MAX_IMAGE_SIZE) {
        toastError("画像ファイルは10MB以下にしてください");
        return false;
      }
      return true;
    });

    if (validPastedFiles.length > 0) {
      append(validPastedFiles.map((file) => ({ file })));
    }
  };

  // Handle remove failed upload file
  const handleRemoveFailedUpload = useCallback((index: number) => {
    setFailedUploads((prev) => {
      const newFailedUploads = [...prev];
      newFailedUploads.splice(index, 1);
      return newFailedUploads;
    });
  }, []);

  return (
    <div>
      {/* Show list files upload  */}
      <div
        className="flex flex-wrap items-center gap-4 text-sm p-2 pt-4 border-t"
        style={{
          display: fields.length || failedUploads.length ? "flex" : "none",
        }}
      >
        {/* Render failed upload files */}
        {failedUploads.map((file, index) => (
          <FileUploadIcon
            key={index}
            fileItem={file}
            handleRemoveFile={() => handleRemoveFailedUpload(index)}
            uploadStatus={-1}
          />
        ))}

        {/* Render form file items */}
        {fields.map((fileItem, index) => (
          <FileUploadIcon
            key={fileItem.id}
            fileItem={fileItem}
            handleRemoveFile={() => handleRemoveFile(index)}
            uploadStatus={uploadStatuses[index]}
          />
        ))}
      </div>

      {/* Menu editor  */}
      <MenuBar editor={editor} />

      {/* Chat form input */}
      <div className="flex items-center gap-1 border-t px-1">
        {/* Upload file from computer  */}
        <button
          className="rounded-lg flex items-center justify-center gap-2 hover:bg-grey-200 transition-colors duration-100 size-[36px] shrink-0"
          onClick={handleUploadClick}
          title="ファイルを選択"
        >
          <input {...getInputProps()} />
          <img src="/svgs/chat_file_upload.svg" width={20} height={20} />
        </button>

        <form
          onSubmit={handleSubmit(handleSendMessage)}
          className="flex items-center gap-1 w-full"
        >
          {/* Input message  */}
          <Controller
            control={control}
            name="message"
            render={({ field }) => (
              <EditorInput
                editor={editor}
                onChange={(v) => {
                  field.onChange(v);
                }}
                onKeyDown={handleKeyDown}
                onPasteFile={handleOnPaste}
              />
            )}
          />

          {/* Button send message  */}
          <button
            type="submit"
            className="rounded-lg flex items-center justify-center hover:bg-grey-200 transition-colors duration-100 size-[36px]"
            title="送信"
            disabled={isSending}
          >
            <IoMdSend
              className={cn(
                "text-light_secondary size-[22px]",
                formState.isValid && "text-cyan"
              )}
            />
          </button>
        </form>
      </div>

      {/* Absolute Dropzone  */}
      {isDragOver && (
        <div
          {...getRootProps()}
          className="absolute bg-black/20 z-50 inset-0 flex items-center justify-center"
        >
          <MdFileUpload className="size-10" />
        </div>
      )}
    </div>
  );
};

interface FileUploadIconProps {
  fileItem: { file: File; id?: string };
  handleRemoveFile: () => void;
  uploadStatus?: number;
}
const FileUploadIcon: React.FC<FileUploadIconProps> = ({
  fileItem,
  handleRemoveFile,
  uploadStatus,
}: FileUploadIconProps) => {
  const isUploadFailed = uploadStatus === -1;
  const isUploading = uploadStatus === 1;
  const showDeleteButton = !isUploading && !isUploadFailed;

  const isMedia =
    checkImageFromMimeType(fileItem.file.type) ||
    checkVideoFromMimeType(fileItem.file.type);

  return (
    <div className="relative group">
      {/* File image preview media or document */}
      {isMedia ? (
        <AttachmentFileIcon
          file={fileItem.file}
          className={cn(
            "object-cover rounded-lg",
            isMedia ? "size-10" : "size-10"
          )}
          mimeType={fileItem.file.type}
          name={fileItem.file.name}
        />
      ) : (
        <div className="flex items-center bg-grey-200 rounded-lg p-2 w-fit gap-2">
          <AttachmentFileIcon
            file={fileItem.file}
            className={cn("object-cover rounded-lg size-6")}
            mimeType={fileItem.file.type}
            name={fileItem.file.name}
            render={() => (
              <img
                src="/svgs/document_icon.svg"
                alt="file"
                className="size-6"
              />
            )}
          />
          <div>
            <div className="max-w-40 truncate line-clamp-1">
              {fileItem.file.name}
            </div>
            {isUploading && (
              <div className="flex items-center gap-2 text-xs">
                <div>
                  {Math.round(fileItem.file.size / 1024).toLocaleString()} KB
                </div>
                <AiOutlineLoading3Quarters className="animate-spin size-3" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Button remove file */}
      {showDeleteButton && (
        <button
          className="bg-grey-200 rounded-full size-5 flex items-center justify-center absolute top-[-10px] right-[-10px] hover:bg-grey-300 transition-colors duration-100"
          onClick={handleRemoveFile}
        >
          <MdClose className="size-4" />
        </button>
      )}

      {/* Uploading statuses */}
      {isUploading && isMedia && (
        <div className="absolute inset-0 flex items-center justify-center bg-grey-400/70 rounded-lg">
          <AiOutlineLoading3Quarters className="animate-spin" />
        </div>
      )}

      {/*Upload fail status*/}
      {isUploadFailed && (
        <div className="absolute inset-0 flex items-center justify-center bg-grey-400/70 rounded-lg">
          <MdErrorOutline
            className="text-error_main size-6 cursor-pointer"
            onClick={handleRemoveFile}
            title="消去"
          />
        </div>
      )}
    </div>
  );
};
export default ChatInput;
